import { PointerEvent, useState, useCallback, useMemo } from 'react';
import { css } from '@emotion/react';
import { Plus } from '@phosphor-icons/react';
import { useEditAppContext } from 'client/lib/EditApp';
import { trpc, useQuery } from 'client/lib/trpc';
import { useAccount } from 'client/lib/User';
import { useProjectState } from 'client/state/ProjectState';
import { DashboardOnboarding } from 'client/pages/dashboard/components/DashboardOnboarding';
import { deploymentSortComparator } from 'client/pages/deployment/deploymentUtils';
import { Button } from 'client/components/Button';
import { Empty } from 'client/components/Empty';
import { Content, Sidebar } from 'client/components/Layout/Dashboard';
import { Menu } from 'client/components/Menu/Menu';
import { Shade, Skeleton } from 'client/components/Skeleton';
import { StackPreview } from 'client/components/StackPreview';
import vscodeLogo from 'client/static/image/vscode.svg';
import { DeploymentItem } from './components/DeploymentItem';

export const ListSkeleton = () => {
  return (
    <Shade>
      <Skeleton type="rect" height={306} />;
    </Shade>
  );
};

export const ProjectDeploymentsTab = () => {
  const account = useAccount();
  const projectID = useProjectState((state) => state.document?.id || null);
  const stackConfig = useProjectState((state) => state.document?.stackConfig);

  const { data, isInitialLoading } = useQuery(
    trpc.project.deployments,
    { projectID: projectID as number },
    {
      enabled: !!projectID,
      refetchInterval: 30 * 1000, // Refetch every 30 secs
    }
  );

  const deployments = data?.deployments || [];
  const pins = data?.pins || [];

  const editAppContext = useEditAppContext();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleMenuOpen = useCallback((event: PointerEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget), []);

  const isReady = editAppContext.isReady && !isInitialLoading;
  const isEmpty = deployments.length <= 0;
  const isOnboardingShown = (account?.projects.length || 0) <= 1 && isEmpty;

  // Order deployments by pins + type + ID
  const sortedDeployments = useMemo(() => [...deployments].sort((a, b) => deploymentSortComparator(a, b, pins)), [deployments, pins]);

  return (
    <>
      <Content style={{ overflowAnchor: 'none' }}>
        {!isReady && <ListSkeleton />}
        {isReady && (
          <>
            {isOnboardingShown && <DashboardOnboarding />}
            {isEmpty && !isOnboardingShown && (
              <Empty subtitle="This project has no deployments. Let's create one!" css={emptyStyle}>
                <Button primary icon={<Plus weight="bold" />} href="deployment/create">
                  Create Deployment
                </Button>
              </Empty>
            )}
            {sortedDeployments.map((deployment, index) => (
              <DeploymentItem
                key={deployment.id}
                index={index}
                deployment={deployment}
                link={`deployment/${deployment.id}`}
                pin={pins.includes(deployment.id)}
                onMenu={handleMenuOpen}
              />
            ))}
          </>
        )}
      </Content>

      <Sidebar>
        <StackPreview title="Project Stack" description="Stack preview from the default branch." stackConfig={stackConfig} />
      </Sidebar>

      <Menu
        items={[
          {
            id: 'vscode',
            title: 'Open in VS Code',
            image: { alt: 'vs code icon', src: vscodeLogo },
            href: anchorEl?.getAttribute('data-url') || '#',
            absolute: true,
          },
        ]}
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={() => setAnchorEl(null)}
        css={menuStyle}
      />
    </>
  );
};

const menuStyle = css`
  .MuiList-root {
    width: 260px;
  }

  .MuiAvatar-img {
    padding: 4px;
  }
`;

const emptyStyle = css`
  margin-top: 32px;
`;
